import "src/assets/css/odq-and-reliability-review.scss";
import React, {useEffect, useState} from "react";
import {CustomLayoutProps} from "src/app/config/LayoutRegistry";
import ReviewSection from "src/app/component/review/ReviewSection";
import Fragment from "src/app/component/review/Fragment";
import {ReviewSectionData} from "src/app/model/ReviewData";
import DefectsPanel from "src/app/component/odqAndReliability/DefectsPanel";
import ODQAndReliabilityDecisionFooter from "src/app/component/odqAndReliability/ODQAndReliabilityDecisionFooter";
import {ReviewEvent, ReviewStatus, ValueType} from "src/app/constant/Enums";
import reviewEventBus from "src/app/eventbus/ReviewEventBus";
import ConfirmationDialog from "src/app/component/dialog/ConfirmationDialog";
import {OdqAndReliabilityApi, GetStandingResponsePayload} from "src/app/api/OdqAndReliabilityApi";
import {KatButton} from "@amzn/katal-react";

const findSection = (sections: ReviewSectionData[], keyword: string): ReviewSectionData | undefined => {
    return sections.find(s => s.title.toLowerCase().includes(keyword.toLowerCase()));
};

const getFragmentData = (section: ReviewSectionData) => {
    return section.fragments instanceof Map
        ? section.fragments.get("main")
        : (section.fragments as any)?.main;
};

const PLACE_HOLDER = "%val";

/** Plain-text form of one element, mirroring how PlainTextElement/HrefElement render it. */
const elementToText = (elementWithType: any): string => {
    const element = elementWithType?.element;
    if (!element?.jsonVal) return '';
    if (element.type === ValueType.LIST) {
        try {
            return (JSON.parse(element.jsonVal) as string[]).join('\n');
        } catch {
            return element.jsonVal;
        }
    }
    if (element.type === ValueType.MAP) {
        try {
            return JSON.parse(element.jsonVal).name ?? element.jsonVal;
        } catch {
            return element.jsonVal;
        }
    }
    return element.jsonVal;
};

/**
 * Flattens a config-driven section into copy-pasteable text: each row's context with
 * its %val placeholders substituted, one row per line. Stays config-driven — nothing
 * about the questions is hardcoded here.
 */
const sectionToText = (section: ReviewSectionData): string => {
    const rows = getFragmentData(section)?.rows ?? [];
    return rows
        .map((row: any) => row.context
            .split(PLACE_HOLDER)
            .map((segment: string, idx: number) => segment + elementToText(row.elements?.[idx]))
            .join(''))
        .join('\n');
};

const buildTcorpUrl = (referenceId: string) => {
    const ticketSearchFilter = `{"AND":{"status":{"OR":["Assigned",{"OR":["Researching",{"OR":["Work In Progress",{"OR":["Pending",{"OR":["Resolved","Closed"]}]}]}]}]},"AND":{"severity":{"OR":["1",{"OR":["2",{"OR":["2.5",{"OR":["3",{"OR":["4",{"OR":["5","N"]}]}]}]}]}]},"keyword":"( ${referenceId})"}}}`;
    return `https://t.corp.amazon.com/issues?q=${encodeURIComponent(ticketSearchFilter)}`;
};

/**
 * ODQAndReliabilityReview - Main layout component for ODQ and RELIABILITY case types.
 * Registered in LayoutRegistry under "ODQ_AND_RELIABILITY_CUSTOM_LAYOUT".
 *
 * Config-driven sections (from reviewSections): Case Details, Audit Checks, Case History
 * Custom components: DefectsPanel, Calculate button, ODQDecisionFooter
 */
const ODQAndReliabilityReview: React.FC<CustomLayoutProps> = ({reviewId, data}) => {

    const [standing, setStanding] = useState<GetStandingResponsePayload | null>(null);
    const [standingLoading, setStandingLoading] = useState(false);
    const [standingError, setStandingError] = useState<string | null>(null);
    const [auditChecksCopied, setAuditChecksCopied] = useState(false);

    const opportunityType = data.caseSubType === 'ODQ' ? 'DELIVERY' : 'RESERVATION';

    const ODQ_SCORE_LABELS: Record<string, string> = {
        'ON_TIME_DELIVERIES': 'On-time Delivery',
        'DELIVERED_NOT_RECEIVED': 'Delivered and Received',
        'DELIVERY_COMPLETION_RATE': 'Delivery Completion',
        'UNRETURNED': 'Unreturned',
    };
    const RELIABILITY_SCORE_LABELS: Record<string, string> = {
        'RELIABILITY': 'Arrival and Pickup',
        'NO_SHOW': 'On-time Arrival',
        'LATE_CANCEL': 'On-time Cancel',
    };
    const SCORE_LABELS = data.caseSubType === 'ODQ' ? ODQ_SCORE_LABELS : RELIABILITY_SCORE_LABELS;

    const getStandingColor = (bucket?: string): string => {
        if (!bucket) return 'odq-standing--unknown';
        const b = bucket.toUpperCase();
        if (b === 'FANTASTIC' || b === 'GREAT') return 'odq-standing--great';
        if (b === 'GOOD') return 'odq-standing--good';
        if (b === 'FAIR') return 'odq-standing--fair';
        return 'odq-standing--atrisk';
    };

    const fetchStanding = async () => {
        setStandingLoading(true);
        setStandingError(null);
        try {
            const response = await OdqAndReliabilityApi.getStanding(data.referenceId, opportunityType);
            setStanding(response.data);
        } catch (err: any) {
            setStandingError(err.message || 'Failed to fetch standing');
        } finally {
            setStandingLoading(false);
        }
    };

    useEffect(() => {
        fetchStanding();
    }, [data.referenceId]);

    const copyAuditChecks = async (section: ReviewSectionData) => {
        try {
            await navigator.clipboard.writeText(sectionToText(section));
            setAuditChecksCopied(true);
            setTimeout(() => setAuditChecksCopied(false), 2000);
        } catch {
            // Clipboard is unavailable (non-secure context / permission denied) — leave the
            // icon unchanged so the auditor can still select the text manually.
        }
    };

    const caseDetailsSection = findSection(data.reviewSections, "Case Detail");
    const auditChecksSection = findSection(data.reviewSections, "Audit");
    const caseHistorySection = findSection(data.reviewSections, "History");

    return (
        <div className="odq-page">
            <div className="odq-columns">
                {/* LEFT COLUMN: Case Details + Defects */}
                <div className="odq-col-left">
                    {caseDetailsSection && (
                        <>
                            <ReviewSection data={caseDetailsSection} />
                            <div className="odq-links-row">
                                <span style={{fontWeight: 600}}>Links: </span>
                                <a href={buildTcorpUrl(data.referenceId)} target="_blank" rel="noreferrer">T.corp</a>
                                {' '}
                                <a href="https://www.bing.com/maps" target="_blank" rel="noreferrer">Bing Maps</a>
                            </div>
                        </>
                    )}
                    <DefectsPanel caseId={reviewId}
                                  caseSubType={data.caseSubType}
                                  transporterId={data.referenceId}
                                  country={data.country}
                                  pdSnapshot={data.pdSnapshot} />
                </div>

                {/* RIGHT COLUMN: Audit Checks + Standing + Case History */}
                <div className="odq-col-right">
                    {/* Audit Checks: config-driven rows, rendered outside KatCard so the list scrolls. */}
                    {auditChecksSection && (
                        <div className="odq-card odq-card--flex">
                            <div className="odq-card__header">
                                <div className="odq-card__title">{auditChecksSection.title}</div>
                                <span className="odq-copy-wrapper">
                                    <button className="odq-copy-btn"
                                            onClick={() => copyAuditChecks(auditChecksSection)}
                                            title="Copy audit checks"
                                            aria-label="Copy audit checks"
                                            data-testid="odq-audit-copy">
                                        ⧉
                                    </button>
                                    {auditChecksCopied && (
                                        <span className="odq-copy-toast" role="status" data-testid="odq-audit-copy-toast">
                                            {/* Outlined check-circle. Katal only ships the filled
                                                check_circle, which reads as a solid green disc. */}
                                            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
                                                <path d="M7.5 12.5l3 3 6-6.5" fill="none" stroke="currentColor"
                                                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            Audit Checks copied
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="odq-audit">
                                <Fragment data={getFragmentData(auditChecksSection)?.rows ?? []} />
                            </div>
                        </div>
                    )}

                    {/* Standing: fetched from FlexCyberno via GetStanding API */}
                    <div className="odq-card">
                        {standingLoading && <div className="odq-standing__value">Loading...</div>}
                        {standingError && <div className="odq-standing__value odq-standing--error">Failed to load standing</div>}
                        {standing && standing.panelStatus === 'DEGRADED' && (
                            <div className="odq-standing__value odq-standing--error">Standing data unavailable (degraded)</div>
                        )}
                        {standing && standing.panelStatus === 'OK' && (
                            <div className="odq-standing__panel">
                                <div className="odq-standing__overall">
                                    Overall Standing – <span className={getStandingColor(standing.overallStanding)}>
                                        {standing.overallStanding?.split('_').join(' ') || 'N/A'}
                                    </span>
                                </div>

                                <div className="odq-standing__scores">
                                    {Object.entries(SCORE_LABELS).map(([scoreType, label]) => {
                                        const score = standing.scores?.find(s => s.scoreType === scoreType);
                                        return (
                                            <div className="odq-standing__score-row" key={scoreType}>
                                                <span className="odq-standing__score-label">{label}</span>
                                                <span> – </span>
                                                <span className={getStandingColor(score?.standingBucket)}>
                                                    {score?.standingBucket?.split('_').join(' ') || 'N/A'}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Date Created identifies WHICH snapshot is on screen; Data Freshness
                                    advances on every recalculation by design, so it cannot. Both are
                                    shown because auditors treat Date Created as their source of truth
                                    and had been reading freshness in its place. */}
                                <div className="odq-standing__score-row">
                                    <span className="odq-standing__score-label">Date Created (IST):</span>
                                    <span> </span>
                                    <span data-testid="standing-created-date">{standing.createdDate
                                        ? new Date(standing.createdDate).toLocaleString('en-IN', {timeZone: 'Asia/Kolkata'})
                                        : 'N/A'}
                                    </span>
                                </div>

                                <div className="odq-standing__score-row">
                                    <span className="odq-standing__score-label">Data Freshness Date (IST):</span>
                                    <span> </span>
                                    <span data-testid="standing-freshness-date">{standing.dataFreshnessDate
                                        ? new Date(standing.dataFreshnessDate).toLocaleString('en-IN', {timeZone: 'Asia/Kolkata'})
                                        : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="odq-standing__actions">
                            <KatButton label="Recalculate Standings" className="odq-calculate-btn" onClick={fetchStanding} disabled={standingLoading} />
                        </div>
                    </div>

                    {/* Case History: config-driven — disappears when removed from sectionTemplateConfigs */}
                    {caseHistorySection && (
                        <div className="odq-card odq-card--flex">
                            <div className="odq-card__title">Case History</div>
                            <div className="odq-history">
                                {(() => {
                                    const fragments = getFragmentData(caseHistorySection);
                                    const listJson = fragments?.rows[0]?.elements[0]?.element?.jsonVal;
                                    if (!listJson || listJson === "N/A") return null;
                                    try {
                                        const entries: string[] = JSON.parse(listJson);
                                        return entries.map((entry, idx) => (
                                            <div className="odq-history__entry" key={idx}>{entry}</div>
                                        ));
                                    } catch { return null; }
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {ReviewStatus.PENDING === data.reviewStatus && (
                <ODQAndReliabilityDecisionFooter reviewId={reviewId}
                                                 caseSubType={data.caseSubType}
                                                 country={data.country}
                                                 onSLPClick={() => reviewEventBus.emit(ReviewEvent.SHOW_SLP_TICKET_MODAL, true)} />
            )}

            <ConfirmationDialog openDialogEvent={ReviewEvent.SHOW_REVIEW_CONFIRMATION_DIALOG}
                                closeDialogEvent={ReviewEvent.SUBMIT_ODQ_DECISION}
                                eventBus={reviewEventBus}
                                content="Do you confirm to submit the review decision?"
                                data-testid="odq-confirmation-dialog" />
        </div>
    );
};

export default ODQAndReliabilityReview;
